/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: GlobalSearchModal.tsx
 🗓️ Created: 2026-01-05
 🧩 Description:
    Global search modal component for searching across modules and items.

 🔧 Notes:
    - Displays search results categorized by type
    - Supports keyboard navigation
    - Shows breadcrumb paths for each result

 🔁 Last Updated: 2026-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Folder, FileText, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchResult, SearchResponse } from '../hooks/useGlobalSearch';
import { useI18n } from '../hooks/useI18n';
import * as LucideIcons from 'lucide-react';

interface GlobalSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    results: SearchResponse;
    isLoading: boolean;
    onResultSelect: (result: SearchResult) => void;
}

// Get icon component by name
const getIconComponent = (iconName?: string | null) => {
    if (!iconName) return FileText;

    // Convert icon name to PascalCase for lucide-react
    const pascalCase = iconName
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');

    return (LucideIcons as any)[pascalCase] || FileText;
};

export default function GlobalSearchModal({
    isOpen,
    onClose,
    searchQuery,
    onSearchChange,
    results,
    isLoading,
    onResultSelect
}: GlobalSearchModalProps) {
    const { t } = useI18n();
    const inputRef = useRef<HTMLInputElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Combine all results for keyboard navigation
    const allResults = [...results.modules, ...results.items];

    // Focus input when modal opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Reset selection when results change
    useEffect(() => {
        setSelectedIndex(0);
    }, [results]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (event.key === 'Enter' && allResults[selectedIndex]) {
            event.preventDefault();
            onResultSelect(allResults[selectedIndex]);
        }
    }, [allResults, selectedIndex, onResultSelect]);

    if (!isOpen) return null;

    const hasResults = results.modules.length > 0 || results.items.length > 0;
    const hasSearch = searchQuery.trim().length > 0;
    const hasMinLength = searchQuery.trim().length >= 3;
    const needsMoreChars = hasSearch && !hasMinLength;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-[10vh] z-[200]"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -20 }}
                    transition={{ duration: 0.15 }}
                    className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-2xl mx-4 overflow-hidden"
                    onClick={e => e.stopPropagation()}
                    onKeyDown={handleKeyDown}
                >
                    {/* Search Input */}
                    <div className="flex items-center gap-3 p-4 border-b border-border">
                        <Search className={`h-5 w-5 transition-colors ${isLoading ? 'text-primary animate-pulse' : 'text-muted-foreground'}`} />
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder={t('common:globalSearch.placeholder', 'Buscar módulos...')}
                            className="flex-1 bg-transparent text-foreground placeholder-muted-foreground outline-none text-lg"
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                        />
                        <div className="flex items-center gap-2">
                            {isLoading && (
                                <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                            )}
                            <kbd className="hidden sm:inline-flex h-6 items-center gap-1 rounded bg-muted px-2 font-mono text-xs text-muted-foreground">
                                esc
                            </kbd>
                            <button
                                onClick={onClose}
                                className="p-1.5 hover:bg-accent rounded-md transition-colors"
                            >
                                <X className="h-4 w-4 text-muted-foreground" />
                            </button>
                        </div>
                    </div>

                    {/* Results */}
                    <div className="max-h-[60vh] overflow-y-auto">
                        {/* Loading State */}
                        {isLoading && !hasResults && (
                            <div className="p-8 text-center">
                                <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" />
                                <p className="mt-3 text-sm text-muted-foreground">{t('common:globalSearch.searching', 'Buscando...')}</p>
                            </div>
                        )}

                        {/* Empty State */}
                        {!isLoading && !hasSearch && (
                            <div className="p-8 text-center">
                                <Search className="h-12 w-12 mx-auto text-muted-foreground/30" />
                                <p className="mt-3 text-sm text-muted-foreground">
                                    {t('common:globalSearch.hint', 'Escribe para buscar módulos')}
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground/60">
                                    {t('common:globalSearch.keyboard', 'Presiona / o Ctrl+K para abrir búsqueda')}
                                </p>
                            </div>
                        )}

                        {/* Needs More Characters */}
                        {!isLoading && needsMoreChars && (
                            <div className="p-8 text-center">
                                <Search className="h-12 w-12 mx-auto text-muted-foreground/30" />
                                <p className="mt-3 text-sm text-muted-foreground">
                                    {t('common:globalSearch.minChars', 'Escribe al menos 3 caracteres para buscar')}
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground/60">
                                    {3 - searchQuery.trim().length} {t('common:globalSearch.charsRemaining', 'caracteres más necesarios')}
                                </p>
                            </div>
                        )}

                        {/* No Results */}
                        {!isLoading && hasMinLength && !hasResults && (
                            <div className="p-8 text-center">
                                <Search className="h-12 w-12 mx-auto text-muted-foreground/30" />
                                <p className="mt-3 text-sm text-muted-foreground">
                                    {t('common:globalSearch.noResults', 'No se encontraron resultados')}
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground/60">
                                    {t('common:globalSearch.tryDifferent', 'Intenta con términos diferentes')}
                                </p>
                            </div>
                        )}

                        {/* Modules Section */}
                        {results.modules.length > 0 && (
                            <div className="py-2">
                                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                                    <Folder className="h-3.5 w-3.5" />
                                    {t('common:globalSearch.modules', 'Módulos')}
                                    <span className="text-primary">({results.modules.length})</span>
                                </div>
                                {results.modules.map((result, index) => {
                                    const Icon = getIconComponent(result.icon);
                                    const isSelected = index === selectedIndex;

                                    return (
                                        <button
                                            key={`module-${result.id}`}
                                            onClick={() => onResultSelect(result)}
                                            className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${isSelected
                                                ? 'bg-primary/10 border-l-2 border-primary'
                                                : 'hover:bg-accent border-l-2 border-transparent'
                                                }`}
                                        >
                                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary/20' : 'bg-accent'}`}>
                                                <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">
                                                    {result.title}
                                                </p>
                                                <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                                    {result.fullPath.split(' / ').map((part, i, arr) => (
                                                        <span key={i} className="flex items-center gap-1">
                                                            {part}
                                                            {i < arr.length - 1 && <ChevronRight className="h-3 w-3 flex-shrink-0" />}
                                                        </span>
                                                    ))}
                                                </p>
                                            </div>
                                            {isSelected && (
                                                <kbd className="hidden sm:inline-flex h-5 items-center rounded bg-muted px-1.5 font-mono text-xs text-muted-foreground">
                                                    enter
                                                </kbd>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Items Section (future use) */}
                        {results.items.length > 0 && (
                            <div className="py-2 border-t border-border">
                                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                                    <FileText className="h-3.5 w-3.5" />
                                    {t('common:globalSearch.items', 'Items')}
                                    <span className="text-primary">({results.items.length})</span>
                                </div>
                                {results.items.map((result, index) => {
                                    const globalIndex = results.modules.length + index;
                                    const isSelected = globalIndex === selectedIndex;

                                    return (
                                        <button
                                            key={`item-${result.id}`}
                                            onClick={() => onResultSelect(result)}
                                            className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left ${isSelected
                                                ? 'bg-primary/10 border-l-2 border-primary'
                                                : 'hover:bg-accent border-l-2 border-transparent'
                                                }`}
                                        >
                                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary/20' : 'bg-accent'}`}>
                                                <FileText className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">
                                                    {result.title}
                                                </p>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {result.fullPath}
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {hasResults && (
                        <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
                            <span>
                                {results.total} {results.total === 1 ? t('search.result', 'resultado') : t('search.results', 'resultados')}
                            </span>
                            <div className="flex items-center gap-2">
                                <span className="hidden sm:inline">
                                    <kbd className="inline-flex h-5 items-center rounded bg-muted px-1.5 font-mono">↑</kbd>
                                    <kbd className="inline-flex h-5 items-center rounded bg-muted px-1.5 font-mono ml-1">↓</kbd>
                                    <span className="ml-1">{t('search.toNavigate', 'para navegar')}</span>
                                </span>
                                <span className="hidden sm:inline ml-2">
                                    <kbd className="inline-flex h-5 items-center rounded bg-muted px-1.5 font-mono">enter</kbd>
                                    <span className="ml-1">{t('search.toSelect', 'para seleccionar')}</span>
                                </span>
                            </div>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
